import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, Info, X } from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────────────

export type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

// ─── Module-level pub/sub ────────────────────────────────────────────────────

type Listener = (toast: Toast) => void;
const listeners: Listener[] = [];

export function showToast(message: string, type: ToastType = 'info') {
  const toast: Toast = { id: `${Date.now()}-${Math.random()}`, message, type };
  listeners.forEach((fn) => fn(toast));
}

function subscribe(fn: Listener) {
  listeners.push(fn);
  return () => {
    const idx = listeners.indexOf(fn);
    if (idx !== -1) listeners.splice(idx, 1);
  };
}

// ─── Icons ───────────────────────────────────────────────────────────────────

const ICON: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />,
  error:   <XCircle      className="w-4 h-4 text-red-400 shrink-0" />,
  info:    <Info         className="w-4 h-4 text-blue-400 shrink-0" />,
};

const BAR: Record<ToastType, string> = {
  success: 'bg-emerald-500',
  error:   'bg-red-500',
  info:    'bg-blue-500',
};

// ─── ToastItem ───────────────────────────────────────────────────────────────

const ToastItem: React.FC<{ toast: Toast; onDismiss: (id: string) => void }> = ({ toast, onDismiss }) => {
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(toast.id), 4000);
    return () => clearTimeout(timer);
  }, [toast.id, onDismiss]);

  return (
    <div
      role="alert"
      aria-live="polite"
      className="relative flex items-start gap-3 w-80 bg-slate-900 border border-slate-700 text-white rounded-2xl shadow-2xl px-4 py-3 overflow-hidden animate-in slide-in-from-bottom-4 duration-200"
    >
      {ICON[toast.type]}
      <p className="text-sm font-medium flex-1 leading-snug">{toast.message}</p>
      <button
        onClick={() => onDismiss(toast.id)}
        className="text-slate-500 hover:text-slate-300 transition-colors shrink-0"
        aria-label="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      {/* Auto-dismiss progress bar */}
      <div className={`absolute bottom-0 left-0 h-0.5 ${BAR[toast.type]} animate-[shrink_4s_linear_forwards]`} style={{ width: '100%' }} />
    </div>
  );
};

// ─── ToastContainer ───────────────────────────────────────────────────────────

export const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    return subscribe((toast) => {
      setToasts((prev) => [...prev, toast]);
    });
  }, []);

  const dismiss = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] flex flex-col gap-2 items-center pointer-events-none">
      {toasts.map((t) => (
        <div key={t.id} className="pointer-events-auto">
          <ToastItem toast={t} onDismiss={dismiss} />
        </div>
      ))}
    </div>
  );
};
